/**
 * Strips layout-affecting markup out of HTML pasted into a `csiContenteditable` field.
 *
 * Word and Google Docs put `position`, negative margins, fixed widths and line heights on
 * everything they copy. Pasted verbatim into a contenteditable those styles escape the box:
 * paragraphs paint on top of each other and the content scrolls sideways. We keep the text
 * formatting the author copied (bold, italic, lists, links, paragraphs) and drop anything
 * that can move content out of the normal flow.
 */

// Only these declarations survive a paste. Everything else is layout.
const ALLOWED_STYLE_PROPERTIES = [
  'font-weight',
  'font-style',
  'font-variant',
  'text-decoration',
  'text-align',
  'color',
  'background-color'
];

// Elements that have no business in a block of rich text.
const DISALLOWED_TAGS = [
  'SCRIPT',
  'STYLE',
  'LINK',
  'META',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'FORM',
  'INPUT',
  'BUTTON',
  'SELECT',
  'TEXTAREA'
];

// Attributes that either carry layout or belong to the source document, not ours.
const DISALLOWED_ATTRIBUTES = ['class', 'id', 'width', 'height', 'align', 'valign', 'bgcolor'];

export function sanitizeContenteditableHtml(html: string): string {
  if (html === null || html === undefined || html === '') {
    return '';
  }

  // Detached container - this is never attached to the document, so nothing in it runs.
  const container = document.createElement('div');
  container.innerHTML = html;

  sanitizeChildren(container);

  return container.innerHTML;
}

function sanitizeChildren(parent: Node) {
  // Snapshot: we mutate the tree while walking it.
  const children: Node[] = Array.prototype.slice.call(parent.childNodes);

  children.forEach(node => {
    if (node.nodeType === Node.COMMENT_NODE) {
      // Drops MSO conditional comments (`<!--[if gte mso 9]>...`).
      parent.removeChild(node);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;

    if (DISALLOWED_TAGS.indexOf(element.tagName) >= 0) {
      parent.removeChild(element);
      return;
    }

    sanitizeChildren(element);

    if (isUnknownElement(element)) {
      // Word namespaced cruft (`<o:p>`, `<w:sdt>`) - keep the text, lose the wrapper.
      unwrap(parent, element);
      return;
    }

    sanitizeAttributes(element);
  });
}

function sanitizeAttributes(element: HTMLElement) {
  const attributeNames: string[] = Array.prototype.slice
    .call(element.attributes)
    .map((attribute: Attr) => attribute.name);

  attributeNames.forEach(name => {
    const lowerCaseName = name.toLowerCase();

    if (lowerCaseName.indexOf('on') === 0) {
      element.removeAttribute(name);
      return;
    }

    if (DISALLOWED_ATTRIBUTES.indexOf(lowerCaseName) >= 0) {
      element.removeAttribute(name);
      return;
    }

    if (lowerCaseName === 'style') {
      const style = sanitizeStyle(element.getAttribute(name));
      if (style) {
        element.setAttribute(name, style);
      } else {
        element.removeAttribute(name);
      }
    }
  });
}

/**
 * Keeps only whitelisted declarations. Parsing on `;` and the first `:` (rather than
 * regex-replacing property names) is what keeps `border-width` and `stroke-width` intact while
 * still dropping `width`.
 */
function sanitizeStyle(style: string): string {
  if (!style) {
    return '';
  }

  return style
    .split(';')
    .map(declaration => {
      const separatorIndex = declaration.indexOf(':');
      if (separatorIndex < 0) {
        return '';
      }

      const property = declaration
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();
      const value = declaration.slice(separatorIndex + 1).trim();

      if (!value || ALLOWED_STYLE_PROPERTIES.indexOf(property) < 0) {
        return '';
      }

      return property + ': ' + value;
    })
    .filter(declaration => !!declaration)
    .join('; ');
}

function isUnknownElement(element: HTMLElement): boolean {
  // Namespaced tags (`o:p`, `w:sdt`) are the Word/Docs giveaway. HTMLUnknownElement alone is
  // not enough - custom elements and `<csi-*>` would match it too.
  return element.tagName.indexOf(':') >= 0;
}

function unwrap(parent: Node, element: HTMLElement) {
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}
